/**
 * How complete a client's fact-find actually is.
 *
 * ONE definition, shared by the server (which stamps it onto client_profiles at save time so the
 * Partner Portal's client list can show it without decrypting anything) and the frontend. Before
 * this existed the portal simply showed every real client as "50%" — a hardcoded placeholder, not a
 * measurement — so a finished file and an empty one looked identical in the pipeline.
 *
 * Measured against the SEVEN DATA-GATHERING STEPS of the fact-find UI (ClientProfileSteps.tsx),
 * because that is what the adviser and the client both see: "5 of 7 done" is answerable by looking
 * at the form, whereas a field-count percentage is not. A step counts as done when it holds the
 * data the advice engine actually needs from it — not merely when it has been visited, which is
 * what the old `clientProfileStep / 7` progress measured and why simply opening the last step read
 * as 95%.
 *
 * The UI's Step 1 ("who is completing this form?") is deliberately NOT one of them: it always
 * holds an answer, defaulting to the applicant themselves, so it can neither be incomplete nor
 * distinguish a finished file from an empty one. That is why the UI has eight steps and this has
 * seven, and why the labels below start at the UI's Step 2.
 *
 * Deliberately tolerant: this reads files written by every past version of the app, so a missing
 * section is "not done", never an error. It never throws.
 */
import type { FactFind, FactFindAssetMap, FactFindLiabilityMap } from '../calculation/factFindTypes.ts';

/** The fact-find's seven steps, in the order the UI presents them. */
export type FactFindStepId =
    | 'your_details'
    | 'goals'
    | 'living_arrangements'
    | 'assets'
    | 'liabilities'
    | 'income_expenses'
    | 'family_estate';

export interface FactFindStepStatus {
    id: FactFindStepId;
    label: string;
    complete: boolean;
}

export interface ProfileCompleteness {
    /** 0–100, rounded — completed steps as a share of the seven. */
    percent: number;
    completedSteps: number;
    totalSteps: number;
    steps: FactFindStepStatus[];
    /** Nothing meaningful entered yet — the file exists but is empty. */
    isEmpty: boolean;
    /** Every step done. What lets the portal treat a file as ready to model. */
    isComplete: boolean;
}

const nonEmpty = (v: unknown): boolean => typeof v === 'string' && v.trim() !== '';
const positive = (v: unknown): boolean => typeof v === 'number' && Number.isFinite(v) && v > 0;
/** A count that has been given, INCLUDING zero — "none" is an answer, not a blank. */
const numberAnswered = (v: unknown): boolean => {
    if (typeof v === 'number') return Number.isFinite(v) && v >= 0;
    // Older files stored form values as the strings the DOM produced (see repairStoredFactFind);
    // reads here still have to cope with one that hasn't been through a save since.
    if (typeof v === 'string' && v.trim() !== '') return Number.isFinite(Number(v)) && Number(v) >= 0;
    return false;
};

/** Any row in any category of an assets/liabilities category map. */
function hasAnyRow(map: FactFindAssetMap | FactFindLiabilityMap | undefined): boolean {
    if (!map || typeof map !== 'object') return false;
    return Object.values(map).some((list) => Array.isArray(list) && list.length > 0);
}

/** Any goal ticked, across the goals bag (values are booleans, plus a couple of string settings). */
function hasAnyGoal(goals: FactFind['goals']): boolean {
    if (!goals || typeof goals !== 'object') return false;
    return Object.values(goals).some((v) => v === true);
}

/**
 * The living-arrangements step is whichever sub-form the client's residency selects, so it is
 * complete when THAT form has its headline figure — not when the family-home form does.
 */
function livingArrangementsComplete(fd: FactFind): boolean {
    const residency = fd.client?.currentResidency || 'own_home';
    switch (residency) {
        // Read straight off the typed fact-find, no local casts: the rent figure is `rentAmount`
        // (factFindSchema.ts's `rentSchema`, which is `additionalProperties: false` and so would
        // strip anything else). A cast here spelt it `weeklyRent`, a field nothing ever writes, so
        // every renting client scored this step incomplete no matter what they entered.
        case 'renting':
            return positive(fd.rent?.rentAmount);
        case 'granny_flat':
            return positive(fd.grannyFlat?.amountPaid);
        case 'retirement_village':
            return positive(fd.retirementVillage?.entryContribution);
        case 'with_family':
            // No figure of its own — answering the residency question IS the substance of this step.
            return true;
        case 'aged_care':
            return true;
        default:
            return positive(fd.home?.value);
    }
}

/**
 * Gifting has been ANSWERED — a gift recorded, or "nothing gifted" explicitly confirmed.
 *
 * OWNER-CAUGHT, 23 Sep 2026. This step counted only living expenses, so skipping the gifting
 * question left the file "complete" with the question never asked. Gifts inside five years are
 * assessed by Centrelink, so "no gifts" and "never asked" produce different advice while looking
 * identical in the data — the same trap `liabilitiesConfirmedNone` exists to close on the step
 * above. The UI already writes `confirmedNone` when the client answers "No — nothing gifted";
 * this simply reads it.
 *
 * Capital expenses are deliberately NOT tested: they are genuinely optional, and requiring them
 * would leave most files permanently incomplete for a section most households never use.
 */
function giftingAnswered(fd: FactFind): boolean {
    const gifts = fd.giftsHistory as { hasGifted?: unknown; confirmedNone?: unknown } | undefined;
    return gifts?.hasGifted === true || gifts?.confirmedNone === true;
}

/**
 * The estate questions have been ANSWERED for a person — "Will in place?" is yes, no or unsure.
 *
 * OWNER-CAUGHT, 24 Sep 2026. The Family/Estate step counted only the children count, and a blank
 * profile seeds that as 0 — a legitimate answer for a childless household, but also what every
 * untouched file carries — so the step read as done, and the whole profile as "Complete", before
 * anyone had opened it. The estate radios have no seed and are only ever set by a person answering,
 * so they are the evidence the children count cannot be. "Unsure" counts: it is an answer.
 */
function estateAnswered(family: FactFind['family'], who: 'client' | 'partner'): boolean {
    const answer = (family as Record<string, unknown> | undefined)?.[`${who}Will`];
    return answer === 'yes' || answer === 'no' || answer === 'unsure';
}

/** A person's details are enough to advise on: who they are and how old they are. */
function personComplete(person: FactFind['client']): boolean {
    return nonEmpty(person?.firstName) && nonEmpty(person?.dob);
}

export function evaluateProfileCompleteness(factFind: FactFind | null | undefined): ProfileCompleteness {
    const fd = (factFind || {}) as FactFind;
    const isCouple = fd.adviceFor === 'couple';

    const steps: FactFindStepStatus[] = [
        {
            id: 'your_details',
            label: 'Your Details',
            // For a couple, BOTH applicants' details drive the modelling (ages set drawdowns, means
            // testing and SAPTO), so a half-filled couple is not a finished step.
            complete: personComplete(fd.client) && (!isCouple || personComplete(fd.partner)),
        },
        { id: 'goals', label: 'Goals', complete: hasAnyGoal(fd.goals) },
        { id: 'living_arrangements', label: 'Living Arrangements', complete: livingArrangementsComplete(fd) },
        { id: 'assets', label: 'Assets', complete: hasAnyRow(fd.assets) },
        {
            id: 'liabilities',
            label: 'Liabilities',
            // "No debts" is a legitimate, complete answer — but an untouched step is not, and the two
            // look identical in the data. `liabilitiesConfirmedNone` is what the UI sets when the
            // client explicitly confirms it; without it, rows are the only positive evidence.
            complete: hasAnyRow(fd.liabilities) || fd.liabilitiesConfirmedNone === true,
        },
        {
            id: 'income_expenses',
            label: 'Income/Expenses',
            complete: positive(fd.expenses?.livingExpenses) && giftingAnswered(fd),
        },
        {
            id: 'family_estate',
            // A childless household answers 0, which is every bit as complete as answering 3 — so
            // this tests for "a number is present", not "a number greater than zero". But 0 is also
            // the seed, so the step needs the estate question answered too (estateAnswered), for
            // both of a couple as Your Details requires both.
            label: 'Family/Estate',
            complete:
                numberAnswered((fd.family as { childrenCount?: unknown } | undefined)?.childrenCount)
                && estateAnswered(fd.family, 'client')
                && (!isCouple || estateAnswered(fd.family, 'partner')),
        },
    ];

    const completedSteps = steps.filter((s) => s.complete).length;
    const totalSteps = steps.length;
    return {
        percent: Math.round((completedSteps / totalSteps) * 100),
        completedSteps,
        totalSteps,
        steps,
        isEmpty: completedSteps === 0,
        isComplete: completedSteps === totalSteps,
    };
}
